/**
 * <optional-addons-component> - optional extras (link pipes, downpipes, sound kits, diffusers)
 * ticked on the product page and added to the cart alongside the system.
 *
 * Companion to <required-tips-component> with the same contract: listen for the SYSTEM's own
 * successful add-to-cart (its CartAddEvent), then add every ticked add-on as its own paid cart
 * line - each is a separate product, separately priced and stocked - with a "Fitted to: <system>"
 * property so the pairing is visible in the cart and on the order. All ticked items go in one
 * request so the drawer refreshes once.
 *
 * Nothing here gates the purchase: no selection (or no JS) means the system simply adds alone.
 * Boxes are unticked after a successful add so a second add-to-cart does not silently duplicate
 * the extras.
 */

import { CartAddEvent, CartUpdateEvent } from '@theme/events';

class OptionalAddonsComponent extends HTMLElement {
  /** @type {string | null} */ #systemProductId = null;
  /** @type {string} */ #systemTitle = '';
  /** @type {boolean} */ #adding = false;
  /** @type {AbortController} */ #abort = new AbortController();

  connectedCallback() {
    this.#systemTitle = this.getAttribute('data-system-title') || '';

    // The system this selector belongs to: the product form in the same section.
    const section = this.closest('.shopify-section') || document;
    const productForm = section.querySelector('product-form-component');
    this.#systemProductId = productForm instanceof HTMLElement ? productForm.dataset.productId || null : null;

    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** All ticked, enabled add-on checkboxes. */
  #tickedBoxes() {
    return /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll('.optional-addons__checkbox:checked:not(:disabled)'),
    ]);
  }

  /** @param {CartAddEvent} event */
  #onCartAdd = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    // Only react to the SYSTEM being added: a genuine product-form add for this product - not our
    // own add, not the required-tips add, and not an errored add.
    if (data.source !== 'product-form-component') return;
    if (data.didError) return;
    if (this.#systemProductId && String(data.productId) !== String(this.#systemProductId)) return;

    const boxes = this.#tickedBoxes();
    if (boxes.length === 0 || this.#adding) return;

    const quantity = Number(data.itemCount) > 0 ? Number(data.itemCount) : 1;
    this.#addAddons(boxes, quantity);
  };

  /**
   * Adds every ticked add-on as its own cart line in a single request, then re-announces the cart
   * so the drawer and cart page reflect the new lines. Mirrors the event shape of product-form.js.
   * @param {HTMLInputElement[]} boxes
   * @param {number} quantity
   */
  async #addAddons(boxes, quantity) {
    this.#adding = true;

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const properties = this.#systemTitle ? { 'Fitted to': this.#systemTitle } : {};
    const payload = {
      items: boxes
        .map((box) => ({
          id: Number(box.dataset.variantId),
          quantity,
          properties,
        }))
        .filter((item) => item.id),
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    };

    if (payload.items.length === 0) {
      this.#adding = false;
      return;
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.status) return; // add failed (e.g. out of stock) - the system line stands alone.

      // Fresh state so a second add-to-cart does not silently duplicate the extras.
      // Announce the untick so listeners (e.g. the selection total) recompute.
      boxes.forEach((box) => {
        box.checked = false;
        box.dispatchEvent(new Event('change', { bubbles: true }));
      });

      let cart = null;
      try {
        cart = await (await fetch('/cart.js')).json();
      } catch {
        /* non-fatal: the event below still refreshes the items section */
      }

      const detail = {
        source: 'optional-addons-component',
        itemCount: payload.items.reduce((sum, item) => sum + item.quantity, 0),
        sections: result.sections,
      };
      this.dispatchEvent(new CartAddEvent(cart ?? undefined, this.id, detail));
      document.dispatchEvent(new CartUpdateEvent(cart ?? {}, this.id, detail));
    } catch (error) {
      console.error('optional-addons: failed to add extras', error);
    } finally {
      this.#adding = false;
    }
  }
}

if (!customElements.get('optional-addons-component')) {
  customElements.define('optional-addons-component', OptionalAddonsComponent);
}
